import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { X, Clock, User } from 'lucide-react';
import { cancelCustomerOrder, requestWaiterForCancellation } from '@/services/tables/customerOrderService';

interface CustomerCancelOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderCreatedAt: string;
  storeName: string;
  onOrderCancelled?: () => void;
}

export function CustomerCancelOrderDialog({
  open,
  onOpenChange,
  orderId,
  orderCreatedAt,
  storeName,
  onOrderCancelled,
}: CustomerCancelOrderDialogProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [canCancel, setCanCancel] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Calculate time since order creation and check if cancellation is allowed
  useEffect(() => {
    if (!open || !orderCreatedAt) return;

    const checkTime = () => {
      const orderCreated = new Date(orderCreatedAt);
      const now = new Date();
      const timeDiffInMinutes = (now.getTime() - orderCreated.getTime()) / (1000 * 60);
      
      if (timeDiffInMinutes > 5) {
        setCanCancel(false);
        setTimeRemaining(0);
      } else {
        setCanCancel(true);
        setTimeRemaining(Math.max(0, 5 - Math.floor(timeDiffInMinutes)));
      }
    };

    checkTime();
    const interval = setInterval(checkTime, 1000); // Check every second

    return () => clearInterval(interval);
  }, [open, orderCreatedAt]);

  const handleCancelOrder = async () => {
    if (!reason.trim()) {
      toast({
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await cancelCustomerOrder(orderId, reason);

      if (result.success) {
        toast({
          title: t('orderCancelled'),
          description: t('orderCancelledDescription'),
        });

        onOpenChange(false);
        setReason('');
        onOrderCancelled?.();
      } else {
        if (result.error === 'orderTooLateToCancel') {
          toast({
            title: t('orderTooLateToCancel'),
            description: t('orderTooLateToCancelMessage'),
            variant: 'destructive',
          });
        } else {
          toast({
            title: t('error'),
            description: result.error || t('cancelOrderError'),
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('cancelOrderError'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestWaiter = async () => {
    if (!reason.trim()) {
      toast({
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await requestWaiterForCancellation(orderId, reason);

      if (result.success) {
        toast({
          title: t('waiterRequested'),
          description: t('waiterRequestedMessage'),
        });

        onOpenChange(false);
        setReason('');
      } else {
        toast({
          title: t('error'),
          description: result.error || t('waiterRequestError'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('waiterRequestError'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (minutes: number) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-red-500" />
            {t('cancelOrder')}
          </DialogTitle>
          <DialogDescription>
            {canCancel 
              ? t('cancelOrderConfirmation')
              : t('orderTooLateToCancelDescription', { storeName })
            }
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {canCancel && timeRemaining > 0 && (
            <Alert className="bg-blue-50 border-blue-200">
              <Clock className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                {t('timeRemainingToCancel', { time: formatTime(timeRemaining) })}
              </AlertDescription>
            </Alert>
          )}

          {!canCancel && (
            <Alert className="bg-amber-50 border-amber-200">
              <User className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                {t('orderTooLateToCancelExplanation', { storeName })}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label htmlFor="reason">{t('reasonForCancellation')}</Label>
            <Textarea
              id="reason"
              placeholder={t('reasonRequiredDescription')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {t('cancel')}
          </Button>
          
          {canCancel ? (
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={isSubmitting || !reason.trim()}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? t('cancelling') : t('confirmCancellation')}
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={handleRequestWaiter}
              disabled={isSubmitting || !reason.trim()}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? t('requesting') : t('requestWaiter')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 